/**
 * VISA/CERTS: Visa and travel document type definitions
 * Frontend mirror of backend/src/data/visaTypes.js
 */

export const VISA_TYPES = {
  // USA / Caribbean
  b1_b2: {
    code: 'b1_b2',
    label: 'B1/B2 Visa',
    description: 'US Business/Tourist visa - required for non-US crew on yachts in US/Caribbean waters',
    region: 'USA / Caribbean',
    validityYears: 10
  },
  c1_d: {
    code: 'c1_d',
    label: 'C1/D Visa',
    description: 'US Transit/Crewmember visa - for crew joining commercial vessels in the US',
    region: 'USA / Caribbean',
    validityYears: 10
  },

  // Europe
  schengen: {
    code: 'schengen',
    label: 'Schengen Visa',
    description: '90-day short-stay visa for the EU passport-free zone',
    region: 'Europe',
    validityYears: 5
  },
  uk_transit: {
    code: 'uk_transit',
    label: 'UK Seafarer Transit Visa',
    description: 'For crew transiting through the UK to join a vessel',
    region: 'Europe',
    validityYears: 2
  },

  // Asia Pacific
  aus_mcv: {
    code: 'aus_mcv',
    label: 'Australian Maritime Crew Visa (988)',
    description: 'For crew arriving in Australia on a non-military vessel',
    region: 'Asia Pacific',
    validityYears: 1
  },
  nz_transit: {
    code: 'nz_transit',
    label: 'New Zealand Transit Visa',
    description: 'For crew transiting through New Zealand',
    region: 'Asia Pacific',
    validityYears: 1
  },
  thailand_crew: {
    code: 'thailand_crew',
    label: 'Thailand Crew Visa',
    description: 'For crew on yachts cruising Thai waters',
    region: 'Asia Pacific',
    validityYears: 1
  },

  // Middle East
  uae_permit: {
    code: 'uae_permit',
    label: 'UAE Entry Permit',
    description: 'Visa/entry permit for crew joining yachts in Dubai, Abu Dhabi',
    region: 'Middle East',
    validityYears: 1
  },

  // Passports & Nationality Documents
  eu_passport: {
    code: 'eu_passport',
    label: 'EU/EEA Passport',
    description: 'Free movement in Europe - no Schengen visa needed',
    region: 'Passports',
    validityYears: 10
  },
  uk_passport: {
    code: 'uk_passport',
    label: 'UK Passport',
    description: 'Post-Brexit: 90-day Schengen access, no separate visa needed',
    region: 'Passports',
    validityYears: 10
  },
  us_passport: {
    code: 'us_passport',
    label: 'US Passport',
    description: 'Can work on US-flagged yachts freely - no B1/B2 needed',
    region: 'Passports',
    validityYears: 10
  },
  caricom: {
    code: 'caricom',
    label: 'CARICOM Passport',
    description: 'Free movement within Caribbean Community nations',
    region: 'Passports',
    validityYears: 10
  },
  seamans_book: {
    code: 'seamans_book',
    label: "Seaman's Book / Discharge Book",
    description: 'Official maritime identity document issued by flag state',
    region: 'Documents',
    validityYears: 10
  },

  // Flag State Documents
  cayman_sdb: {
    code: 'cayman_sdb',
    label: "Cayman Islands Seaman's Discharge Book",
    description: 'For crew on Cayman-flagged vessels (Red Ensign)',
    region: 'Flag State',
    validityYears: 10
  },
  marshall_id: {
    code: 'marshall_id',
    label: "Marshall Islands Seafarer's ID",
    description: 'For crew on Marshall Islands-flagged vessels',
    region: 'Flag State',
    validityYears: 5
  },
  red_ensign: {
    code: 'red_ensign',
    label: 'Red Ensign Group Flag State Endorsement',
    description: 'Covers UK, Cayman, Gibraltar, Isle of Man, Jersey, Guernsey flag states',
    region: 'Flag State',
    validityYears: 5
  }
};

// Grouped by region for UI dropdowns
export const VISA_GROUPS = [
  {
    label: 'USA / Caribbean',
    visas: ['b1_b2', 'c1_d']
  },
  {
    label: 'Europe',
    visas: ['schengen', 'uk_transit']
  },
  {
    label: 'Asia Pacific',
    visas: ['aus_mcv', 'nz_transit', 'thailand_crew']
  },
  {
    label: 'Middle East',
    visas: ['uae_permit']
  },
  {
    label: 'Passports',
    visas: ['eu_passport', 'uk_passport', 'us_passport', 'caricom']
  },
  {
    label: 'Maritime Documents',
    visas: ['seamans_book', 'cayman_sdb', 'marshall_id', 'red_ensign']
  }
];

// Common visas for quick-add buttons
export const COMMON_VISAS = ['b1_b2', 'schengen', 'eu_passport', 'seamans_book'];

/**
 * Get visa label by code
 */
export function getVisaLabel(code) {
  return VISA_TYPES[code]?.label || code;
}

/**
 * Calculate visa status based on expiry date
 */
export function calculateVisaStatus(expiryDate) {
  if (!expiryDate) return 'valid';

  const expiry = new Date(expiryDate);
  const now = new Date();
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

  if (expiry < now) return 'expired';
  if (expiry < thirtyDaysFromNow) return 'renewal_needed';
  return 'valid';
}

/**
 * Get status badge styling
 */
export function getStatusBadgeClass(status) {
  switch (status) {
    case 'expired':
      return 'bg-red-100 text-red-800';
    case 'renewal_needed':
      return 'bg-yellow-100 text-yellow-800';
    case 'valid':
    default:
      return 'bg-green-100 text-green-800';
  }
}

/**
 * Get status label
 */
export function getStatusLabel(status) {
  switch (status) {
    case 'expired':
      return 'Expired';
    case 'renewal_needed':
      return 'Renewal Needed';
    case 'pending':
      return 'Pending';
    case 'valid':
    default:
      return 'Valid';
  }
}
